import {isEditableElement} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {GlobalCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useCallback, useEffect, useRef} from 'react'

import {useCopyRawBlobContents} from '../hooks/use-copy-raw-blob-contents'

interface SelectAllShortcutProps {
  containerRef: React.RefObject<HTMLElement | null> | undefined
  shouldNotOverrideCopy?: boolean
}

/**
 * This component intercepts the select-all shortcut and selects the contents
 * of the given container instead of the contents of the entire document.
 *
 * In addition, it also intercepts the copy shortcut. If the current selection
 * was created through select-all, it will copy the raw blob contents instead
 * of the current selection. This is because when the code blob is virtualized,
 * the current selection will only contain the visible lines.
 */
export default function SelectAllShortcut({containerRef, shouldNotOverrideCopy}: SelectAllShortcutProps) {
  const allSelected = useRef(false)
  const copyRawBlobContents = useCopyRawBlobContents()

  // If the selection changes while we are listening for this event, then that means the selection is no longer the result of Control+A
  const onSelectionChange = useCallback(() => {
    allSelected.current = false

    // eslint-disable-next-line react-hooks/immutability
    document.removeEventListener('selectionchange', onSelectionChange)
  }, [])

  // Clean up the event listener on unmount
  useEffect(() => () => document.removeEventListener('selectionchange', onSelectionChange), [onSelectionChange])

  const selectAll = useCallback(() => {
    // If the user is focused on an editable element, do not override the default,
    // and instead highlight the content of that element
    if (
      document.activeElement &&
      isEditableElement(document.activeElement) &&
      containerRef?.current !== document.activeElement
    ) {
      selectAllContentInElement(document.activeElement as HTMLElement)
      return
    }
    if (containerRef) {
      // If we're not given a container, just let the browser handle the event
      if (!containerRef.current) return

      // Restrict select all to the code blob
      selectAllContentInElement(containerRef.current)

      allSelected.current = true

      // Any changes to the selection now mean that the selection was not the result of Control+A
      setTimeout(() => document.addEventListener('selectionchange', onSelectionChange), 0)
    }
  }, [containerRef, onSelectionChange])

  const copyFullFileContents = useCallback(
    (event: Event) => {
      // If the selection didn't come from select-all, let the browser handle the event
      if (!allSelected.current) return

      event.preventDefault()
      copyRawBlobContents()
    },
    [copyRawBlobContents],
  )

  // We cannot use data-hotkey for this. If we do, then regular copy events will not work properly.
  useEffect(() => {
    if (shouldNotOverrideCopy) return
    window.addEventListener('copy', copyFullFileContents)
    return () => window.removeEventListener('copy', copyFullFileContents)
  }, [copyFullFileContents, shouldNotOverrideCopy])

  return <GlobalCommands commands={{'github:select-all': selectAll}} />
}

function selectAllContentInElement(element: HTMLElement) {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
    element.select()
    return
  }

  const range = document.createRange()
  range.selectNode(element)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}
